import { CdkColumnDef } from '@angular/cdk/table';
import { Component, ViewEncapsulation } from '@angular/core';
import {
    CustomerSummary,
    ListCustomerSummaryRequest,
    ListCustomerSummaryResponse,
    ListCustomerSummaryService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import {
    atlas,
    CUSTOMER_OVERVIEW,
    INSIGHTS_CUSTOMERS_BY_EXPOSURE
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { RouteParams } from '@lsq-frontend/fasttrack-shared/app-constants';
import { Router } from '@angular/router';
import { PopoverPlacement } from 'ngx-smart-popover';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { convertToTimeZone } from 'date-fns-timezone';
import {
    Color,
    getDynamicRouteLink,
    getRouterLinkById,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';

@Component({
    selector: 'top-ten-customers',
    templateUrl: './top-customers-by-exposure.component.html',
    styleUrls: ['./top-customers-by-exposure.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [CdkColumnDef]
})
export class TopCustomersByExposureComponent {
    public customerSummaryListDataSource: LsqApiServiceListSource<
        ListCustomerSummaryResponse,
        ListCustomerSummaryRequest
    >;
    public icons = Icons;
    public color = Color;

    public isCustomLg$ = false;
    public isLgBelow$ = this._responsiveService.lgBelow;
    public isSmBelow$ = this._responsiveService.smBelow;

    public routerLinkCustomersByExposure = getRouterLinkById(atlas, INSIGHTS_CUSTOMERS_BY_EXPOSURE);
    public customerOverviewRouterLinkUrl = getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW);

    public customerHeader = this._i18n({
        value: 'Customer',
        meaning: 'Table column header'
    });
    public arBalanceHeader = this._i18n({
        value: 'AR Balance',
        meaning: 'Table column header'
    });
    public percentOfArHeader = this._i18n({
        value: '% of Total AR',
        meaning: 'Table column header'
    });
    public creditLimitHeader = this._i18n({
        value: 'Credit Limit',
        meaning: 'Table column header'
    });
    public creditUtilizationRateHeader = this._i18n({
        value: 'Credit Utilization Rate',
        meaning: 'Table column header'
    });
    public currentArHeader = this._i18n({
        value: 'Current AR',
        meaning: 'Table column header'
    });
    public ar1To30Header = this._i18n({
        value: 'AR 1-30 Days',
        meaning: 'Table column header'
    });
    public ar31To60Header = this._i18n({
        value: 'AR 31-60 Days',
        meaning: 'Table column header'
    });
    public ar61PlusHeader = this._i18n({
        value: 'AR 61+ Days',
        meaning: 'Table column header'
    });
    public dso12MonthsHeader = this._i18n({
        value: 'DSO (Last 12 Months)',
        meaning: 'Table column header'
    });
    public dso3MonthsHeader = this._i18n({
        value: 'DSO (Last 3 Months)',
        meaning: 'Table column header'
    });

    public tooltipPlacement = PopoverPlacement.Bottom;
    public dso3MonthsPopoverContent = this._i18n({
        value: 'Values with asterisks do not reach the full 3 months timeframe.',
        meaning: 'Table column popover'
    });
    public dso12MonthsPopoverContent = this._i18n({
        value: 'Values with asterisks do not reach the full 12 months timeframe.',
        meaning: 'Table column popover'
    });

    public readonly noDataTitle = this._i18n('No customers by exposure available for this date.');

    constructor(
        private _i18n: I18n,
        public _responsiveService: ResponsiveService,
        private _windowReferenceService: WindowReferenceService,
        private _router: Router,
        private _userService: UserService,
        private _listCustomerSummariesService: ListCustomerSummaryService
    ) {
        const listCustomerSummariesInitialRequest = new ListCustomerSummaryRequest({
            companyKey: this._getCompanyKey(),
            asOfDate: convertToTimeZone(new Date(), { timeZone: 'UTC' }),
            take: 5,
            skip: 0
        });
        this.customerSummaryListDataSource = new LsqApiServiceListSource(
            this._listCustomerSummariesService,
            listCustomerSummariesInitialRequest
        );

        this._windowReferenceService.resizeStream.subscribe(({ width }) => {
            this.populateCustomLg(width);
        });
    }

    public getCustomerLink(link: string, debtorKey: string): string {
        return getDynamicRouteLink(link, { debtorKey });
    }

    public handleMobileCardClick(row: CustomerSummary): void {
        this._router.navigateByUrl(
            getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW).replace(
                RouteParams.DebtorKey,
                `${row.thirdPartyKey}`
            )
        );
    }

    private _getCompanyKey(): string {
        return this._userService.getCompanyKey();
    }

    private populateCustomLg(width: number): void {
        this.isCustomLg$ = width >= 992 && width <= 1355;
    }
}
